import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import {
  getDeviationResourceType,
  getTranslatedDeviationTitle,
} from '../../../DeviationViews/_helpers';
import { DeviationDuperbrowseContext, DeviationLink } from '../../../Duperbrowse';
import DeviationDumbViewWithIcon from '../../../DeviationViews/DumboWithIcon';
import Grid from '../../../Grid/MeasuredGrid';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';

interface Props {
  streamId: string;
  deviations: PapiDeviation[];
  parentDeviation: PapiDeviation;
  maxRows?: number;
  className?: string;
}

const DeviationSidebarGrid: React.FC<React.PropsWithChildren<Props>> = ({
  streamId,
  deviations,
  parentDeviation,
  maxRows,
  className,
}) => {
  const { t } = useTranslation();
  const preloadEnabled = false;

  const renderItem = useCallback(
    ({ height, width, index, style }) => {
      const item = deviations[index];
      return (
        <div style={style} key={index}>
          <DeviationLink
            deviation={item}
            key={item.deviationId}
            title={getTranslatedDeviationTitle(item, t)}
          >
            <DeviationDumbViewWithIcon
              deviation={item}
              width={width}
              height={height}
              greenLikeCrop
              withSeoData
              videoIconSize={IconSize.BIGGER}
            />
          </DeviationLink>
        </div>
      );
    },
    [deviations, t]
  );

  return (
    <DeviationDuperbrowseContext
      streamId={streamId}
      deviations={deviations}
      parent={{
        typeId: getDeviationResourceType(parentDeviation),
        itemId: parentDeviation.deviationId,
      }}
      preloadEnabled={preloadEnabled}
    >
      <Grid
        lazyloadFromContext
        enableScrollOptim={false}
        containerWidth={308}
        defaultSize={{
          desktop: { width: 308, height: 308 },
          mobile: { width: 308, height: 308 },
        }}
        cookieType={MeasuredCookieType.DEVIATION_SIDEBAR}
        maxRows={maxRows}
        breakpoints={[
          {
            maxWidth: 600,
            elementsPerRow: 3,
            elementAspectRatio: 1,
          },
        ]}
        spacing={16}
        elementCount={deviations.length}
        renderElement={renderItem}
      />
    </DeviationDuperbrowseContext>
  );
};
DeviationSidebarGrid.displayName = 'DeviationSidebarGrid';

export default DeviationSidebarGrid;
